import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { AlertBoardView } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { PageRoot, PageHeader } from '../ui/Page';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  isNotFound,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { Notice } from '../ui/Badge';
import { Button, ghostButton } from '../ui/Button';
import { useNow } from '../ui/useNow';
import { IconAlerts } from '../icons';
import { AlertBoard } from '../alerts/AlertBoard';
import { useAlertCount } from '../alerts/AlertCount';
import { AlertPanel } from '../alerts/AlertPanel';
import {
  ALERT_VIEW_TABS,
  alertsSearch,
  emptyBoard,
  headerLine,
  mayConfigureAlerts,
  mayWorkAlerts,
  notMonitoredLine,
  phoneColumn,
  subjectLine,
  type AlertsSearch,
} from '../alerts/alertsView';
import type { AlertDetailData } from '../alerts/types';

/**
 * The Alerts page (ops insights Phase 7 spec §6, prototype 203 R minus its bell): what the
 * monitors found that needs someone, in three columns, for the owner and the office.
 *
 * The view, the phone's column and the open alert all live in the address
 * (`/alerts?view=snoozed&col=parts&alert=<id>`, `alertsSearch`): a link from the bell, the
 * morning email or MCP lands on the same view with the panel open and the card MARKED
 * (`aria-current`) and scrolled into view once. An alert that is not on this view (snoozed,
 * muted, resolved, someone else's on "Mine") is pinned above the board, marked, saying why,
 * with the way to it. The Tickets page's pattern (`TicketsPage.tsx`).
 *
 * ⚠️ FOUR DIFFERENT EMPTIES, NEVER ONE: a failed read (`ErrorState`), no monitor Live yet, some
 * Live monitors not being checked (the health banner names them, and the board says it is not
 * an all-clear), and every Live monitor checked and found nothing (`emptyBoard`).
 */
export function AlertsPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const me = useSession();
  const raw: Record<string, unknown> = useSearch({ strict: false });
  const search = alertsSearch(raw);
  const view: AlertBoardView = search.view ?? 'all';
  const now = useNow();
  /** What the last write that took an alert off the board did, said once. */
  const [said, setSaid] = useState<string | null>(null);

  /** Change the address: a functional update, so what it does not name survives. */
  const setSearch = (patch: Partial<AlertsSearch>) => {
    const merge = (prev: Record<string, unknown>) =>
      Object.fromEntries(
        Object.entries({ ...alertsSearch(prev), ...patch }).filter(
          ([, v]) => v !== undefined,
        ),
      );
    void navigate({
      to: '.',
      // Route-generic (`to: '.'`); the route's own `validateSearch` checks it again.
      search: merge as never,
      replace: true,
      resetScroll: false,
    });
  };

  const board = useQuery({
    ...trpc.alert.board.queryOptions({ view }),
    placeholderData: (prev) => prev,
  });
  const members = useQuery(trpc.org.members.queryOptions());
  // The sidebar's own count (one read, one definition): the header says it as "M urgent".
  // Not a poller: the shown chrome polls it, and this observer shares its answer.
  const urgent = useAlertCount(false);

  // A link lands on the exact card (UX baseline), the Tickets page's rules: marked when it is
  // drawn in this view, pinned above it when it is not, scrolled to ONCE per opened id. The pin
  // outlives the (modal) panel so its way back is reachable, until dismissed or drawn again.
  const openId = search.alert;
  const [pinned, setPinned] = useState<string | undefined>(openId);
  const [pinnedFor, setPinnedFor] = useState(openId);
  if (openId !== pinnedFor) {
    setPinnedFor(openId);
    if (openId !== undefined) {
      setPinned(openId);
      setSaid(null);
    }
  }
  const anchorId = openId ?? pinned;
  const shownIds: readonly string[] | null =
    board.data?.columns.flatMap((c) => c.cards.map((x) => x.id)) ?? null;
  if (pinned !== undefined && shownIds?.includes(pinned)) setPinned(undefined);
  const offView =
    anchorId !== undefined && shownIds !== null && !shownIds.includes(anchorId);
  const linked = useQuery({
    ...trpc.alert.get.queryOptions({ alertId: anchorId ?? '' }),
    enabled: anchorId !== undefined,
  });
  const linkedGone = linked.isError && isNotFound(linked.error);

  const scrolledFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!openId) {
      scrolledFor.current = undefined;
      return;
    }
    if (scrolledFor.current === openId || typeof document.querySelector !== 'function')
      return;
    const el = document.querySelector(
      offView ? '[data-anchor="linked-alert"]' : `[data-alert-key="${openId}"]`,
    );
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      scrolledFor.current = openId;
    }
  }, [openId, offView, board.data, linked.data]);

  const open = (alertId: string) => setSearch({ alert: alertId });
  const b = board.data;
  // A board already read is drawn whatever the network does now (`shownState`).
  const boardState = shownState(board, board.data);
  const tz = b?.timeZone ?? linked.data?.timeZone;
  const nowDate = new Date(now);
  const empty = b && tz ? emptyBoard(b, tz, nowDate) : null;

  return (
    <PageRoot>
      <PageHeader
        title="Alerts"
        sub={b && tz ? headerLine(b, tz, nowDate, urgent) : undefined}
        right={
          mayConfigureAlerts(me.role) ? (
            <Link to="/settings" hash="monitors" className={ghostButton}>
              Alert settings
            </Link>
          ) : undefined
        }
      />

      {b && b.notMonitored.length > 0 ? (
        <div data-health-banner="" className="notice badge--warn space-y-1" role="status">
          {b.notMonitored.map((m) => (
            <p key={m.key}>{notMonitoredLine(m)}</p>
          ))}
          <p className="text-xs">
            Its alerts may be missing from this board until it is checked again.
          </p>
        </div>
      ) : null}

      <UnderlineTabs
        value={view}
        onValueChange={(v) =>
          setSearch({ view: v === 'mine' || v === 'snoozed' ? v : undefined })
        }
      >
        <UnderlineTabStrip tabs={ALERT_VIEW_TABS} />
      </UnderlineTabs>

      {said ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-border bg-surface p-3 text-sm"
        >
          <span className="min-w-0 break-words">{said}</span>
          <Button variant="ghost" size="sm" onClick={() => setSaid(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {offView && anchorId && linkedGone ? (
        <GoneLinkNotice
          onDismiss={() => {
            setPinned(undefined);
            if (openId !== undefined) setSearch({ alert: undefined });
          }}
        />
      ) : offView && anchorId ? (
        <LinkedAlertAnchor
          detail={linked.data}
          view={view}
          me={me.userId}
          onOpen={() => open(anchorId)}
          onShow={(v) =>
            setSearch({ view: v === 'all' ? undefined : v, alert: anchorId })
          }
          onDismiss={openId === undefined ? () => setPinned(undefined) : undefined}
        />
      ) : null}

      {boardState !== 'ready' || !b || !tz ? (
        // Offline before the board was ever read is "offline", never a skeleton that cannot
        // resolve or a "Couldn't load" (queryState checks `isPaused` before `isPending`).
        <UnresolvedState
          state={unresolved(boardState)}
          what="the alerts"
          icon={IconAlerts}
          error={board.error}
          onRetry={() => void board.refetch()}
          skeleton="h-64 w-full"
        />
      ) : (
        <>
          {board.isPaused ? (
            <Notice tone="warn">
              You&apos;re offline: this is the board as it was last read. It will catch up
              when you&apos;re back on a connection.
            </Notice>
          ) : null}
          <StaleNotice
            show={refreshFailed(board, b)}
            onRetry={() => void board.refetch()}
          />
          {empty ? (
            <div data-empty-board={empty.kind}>
              <EmptyState
                icon={IconAlerts}
                title={empty.title}
                description={empty.description}
              />
            </div>
          ) : (
            <AlertBoard
              columns={b.columns}
              view={view}
              openId={openId}
              phoneCol={phoneColumn(b.columns, search.col, openId)}
              mayWork={mayWorkAlerts(me.role)}
              onOpen={open}
              onPickColumn={(col) => setSearch({ col })}
              tz={tz}
              now={now}
            />
          )}
        </>
      )}

      <AlertPanel
        alertId={openId ?? null}
        members={members.data ?? []}
        onClose={() => setSearch({ alert: undefined })}
        onLeft={(s) => {
          setSaid(s);
          setPinned(undefined);
          setSearch({ alert: undefined });
        }}
      />
    </PageRoot>
  );
}

/** Why the opened alert is not on this view, and the view it is on, when there is one. */
function offViewReason(
  a: AlertDetailData,
  view: AlertBoardView,
  me: string,
  now: number,
): { why: string; show?: AlertBoardView; showLabel?: string } {
  if (a.status === 'resolved') return { why: 'is resolved, so it is off the board.' };
  if (a.isTesting)
    return { why: 'is from a monitor in Testing, so it is off the board.' };
  if (a.mute) return { why: 'is muted, so it is off the board.' };
  const snoozed = a.snoozedUntil !== null && new Date(a.snoozedUntil).getTime() > now;
  if (snoozed && view !== 'snoozed') {
    return { why: 'is snoozed.', show: 'snoozed', showLabel: 'Show snoozed' };
  }
  if (!snoozed && view === 'snoozed') {
    return { why: "isn't snoozed.", show: 'all', showLabel: 'Show waiting' };
  }
  if (view === 'mine' && a.owner?.id !== me) {
    return { why: "isn't yours.", show: 'all', showLabel: 'Show waiting' };
  }
  return { why: "isn't among the cards shown." };
}

/**
 * The open alert is not drawn in this view: say so, above it, marked as the linked item, with
 * a way to it (the panel) and, when another view holds it, the way there.
 */
function LinkedAlertAnchor({
  detail,
  view,
  me,
  onOpen,
  onShow,
  onDismiss,
}: {
  detail: AlertDetailData | undefined;
  view: AlertBoardView;
  me: string;
  onOpen: () => void;
  onShow: (view: AlertBoardView) => void;
  /** Offered once the panel is closed; while it is open the anchor is its alert's place. */
  onDismiss: (() => void) | undefined;
}) {
  const reason = detail ? offViewReason(detail, view, me, Date.now()) : null;
  return (
    <div
      data-anchor="linked-alert"
      aria-current="true"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius)] border border-brand bg-[var(--sw-brand-pill)] p-3 text-sm"
    >
      <span className="font-medium">Opened alert:</span>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={onOpen}
        className="min-w-0 break-words text-left font-medium text-[var(--sw-brand-text)] underline underline-offset-2"
      >
        {detail ? `${detail.label} — ${subjectLine(detail, detail.label)}` : 'This alert'}
      </button>
      <span className="text-muted-foreground">
        {reason?.why ?? "isn't on this view."}
      </span>
      {reason?.show ? (
        <Button variant="ghost" size="sm" onClick={() => onShow(reason.show!)}>
          {reason.showLabel}
        </Button>
      ) : null}
      {onDismiss ? (
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      ) : null}
    </div>
  );
}

/** The linked alert is not here (an old link, or Testing): say so, and offer only to dismiss. */
function GoneLinkNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      data-anchor="linked-alert"
      data-gone="true"
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius)] border border-border bg-surface p-3 text-sm"
    >
      <span>
        That alert isn&apos;t here: the link is older than it, or its monitor is in
        Testing.
      </span>
      <Button variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
